import { useCustomizationContext, ThemeJson } from '@apps/card/routes/CardCustomizer';
import { CardPaperType, CardDieCut, CardEnvelopeReturnAddressLayout, CardEnvelopeRecipientAddressLayout } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { useEventCallback } from 'yet-another-react-lightbox';
import { StationeryProduct } from '@shared/utils/print-price/types';
import { AddAttributeFunction } from '../state/state.mapping';
import { getPossibleOptions } from './utils';
import { useGetFoilConfigs } from './useDeriveFoilIds';
import { findFoilLayer } from '@apps/card/utils';

const cardPaperTypeToOptionId = (paperType: CardPaperType): string => {
  switch (paperType) {
    case 'standard': {
      return 'signature';
    }
    default: {
      return paperType;
    }
  }
};

const optionIdToCardPaperType = (optionId: string): CardPaperType => {
  switch (optionId) {
    case 'signature': {
      return CardPaperType.signature;
    }
    default: {
      return optionId as CardPaperType;
    }
  }
};

export const useTaylorMappings = () => {
  const [_customizations, updateCustomizations] = useCustomizationContext(draft => draft.config.customizations);
  const [_root, updateDraftRoot] = useCustomizationContext(draft => draft);
  const { getFoilConfigs } = useGetFoilConfigs();

  return useEventCallback((addAttribute: AddAttributeFunction, product: StationeryProduct) => {
    // Register possible attribute 'paperType'
    const paperTypeId = 'paperType';
    const paperTypeOptions = getPossibleOptions(product, paperTypeId);
    addAttribute(
      {
        productAttributeId: paperTypeId,
        getOptionId: (draftData: ThemeJson) => {
          return cardPaperTypeToOptionId(draftData.customizations.paperType);
        },
        setOptionId: (newState: string) => {
          updateCustomizations(draftData => {
            draftData.paperType = optionIdToCardPaperType(newState);
          });
        },
        allowedOptionIDs: paperTypeOptions,
      },
      product,
    );

    const cardShapeId = 'shape';
    const cardShapeOptions = getPossibleOptions(product, cardShapeId);
    addAttribute(
      {
        productAttributeId: cardShapeId,
        getOptionId: (draftData: ThemeJson) => {
          return draftData.customizations.shape;
        },
        setOptionId: (newState: string) => {
          updateCustomizations(draftData => {
            draftData.shape = newState as CardDieCut;
          });
        },
        allowedOptionIDs: cardShapeOptions,
      },
      product,
    );

    const returnAddressId = 'returnAddress';
    const returnAddressOptions = getPossibleOptions(product, returnAddressId);
    addAttribute(
      {
        productAttributeId: returnAddressId,
        getOptionId: (draftData: ThemeJson) => {
          return draftData.card.envelope.layout;
        },
        setOptionId: (newState: string) => {
          updateDraftRoot(draft => {
            draft.config.card.envelope.layout = newState as CardEnvelopeReturnAddressLayout;
          });
        },
        allowedOptionIDs: returnAddressOptions,
      },
      product,
    );

    const recipientAddressId = 'recipientAddress';
    const recipientAddressOptions = getPossibleOptions(product, recipientAddressId);
    addAttribute(
      {
        productAttributeId: recipientAddressId,
        getOptionId: (draftData: ThemeJson) => {
          return draftData.card.envelope.recipientAddressLayout ?? 'none';
        },
        setOptionId: (newState: string) => {
          updateDraftRoot(draft => {
            draft.config.card.envelope.recipientAddressLayout = newState as CardEnvelopeRecipientAddressLayout;
          });
        },
        allowedOptionIDs: recipientAddressOptions,
      },
      product,
    );

    const foilId = 'foil';
    const foilOptions = getPossibleOptions(product, foilId); //deriveFoilIDs(product, foilId);
    const foilConfigs = getFoilConfigs(product, foilId);
    // Maps target OptionId to real ColorId on set
    // And stored ColorId to OptionId on get
    addAttribute(
      {
        productAttributeId: foilId,
        getOptionId: (draftData: ThemeJson) => {
          // Get colorId from draft
          const foilColorId = findFoilLayer(draftData.card.front.layers)?.foilData?.colorId;
          // Map colorId to optionId
          const foilOptionId = foilConfigs?.find(f => f.colorId === foilColorId)?.optionId;
          // Return optionId
          return foilOptionId ?? 'gold';
        },
        setOptionId: (newState: string) => {
          // Get colorId for matching option
          const foilColorId = foilConfigs?.find(f => f.optionId === newState)?.colorId;
          updateDraftRoot(draft => {
            const layer = findFoilLayer(draft.config.card.front.layers);
            if (layer?.foilData) {
              layer.foilData.colorId = foilColorId ?? 'gold';
            }
          });
        },
        allowedOptionIDs: foilOptions,
      },
      product,
    );

    addAttribute(
      {
        productAttributeId: 'cardBack',
        getOptionId: () => 'free',
        setOptionId: () => {},
        allowedOptionIDs: ['free'],
      },
      product,
    );
  });
};
